import React, { useState } from 'react';
import { FaPaperPlane, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { sendEmailDemo } from '../services/emailService';
import { useToast } from '../contexts/ToastContext';

const HireMePopup = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const { showSuccess, showError } = useToast();

  if (!show) return null;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Use demo email service for now
      const result = await sendEmailDemo(formData, 'hire');
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', message: '' });
        
        // Show success toast
        showSuccess('🎉 Hiring inquiry sent successfully! I\'ll contact you within 24 hours.', 8000);
        
        // Auto close after success
        setTimeout(() => {
          onClose();
          setSubmitStatus('');
        }, 2000);
      } else {
        setSubmitStatus('error');
        showError('Sorry, there was an error sending your inquiry. Please try again.', 8000);
      }

    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      showError('Sorry, there was an error sending your inquiry. Please try again.', 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md relative transform transition-all animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-2xl font-bold text-gray-400 hover:text-gray-600 transition-colors z-10"
          onClick={onClose}
        >
          ×
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 pr-8">
            🚀 Hire Sabarisan P
          </h2>
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-semibold">Front End Developer</span> • PHP Specialist • 3+ Years Experience
          </p>
          <p className="text-gray-600 text-sm">
            Ready to bring a passionate developer with logistics and web development expertise to your team? 
            Let's discuss how I can contribute to your projects!
          </p>
        </div>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="mx-6 mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-start">
            <FaCheckCircle className="mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold mb-1">Hiring inquiry sent successfully! 🎉</p>
              <p>I'll review your requirements and get back to you within 24 hours with my availability and next steps.</p>
            </div>
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div className="mx-6 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold mb-1">Oops! Something went wrong</p>
              <p>Please try again or reach out directly at <a href="mailto:sabarisandeveloper@gmail.com" className="underline">sabarisandeveloper@gmail.com</a></p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block font-medium mb-2 text-gray-700">Your Name *</label>
            <input
              type="text"
              name="name"
              placeholder="HR Manager / Recruiter Name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:border-pink-500 focus:outline-none transition-colors"
              required
            />
          </div>
          
          <div>
            <label className="block font-medium mb-2 text-gray-700">Company Name *</label>
            <input
              type="text"
              name="company"
              placeholder="Your Company"
              value={formData.company}
              onChange={handleInputChange}
              className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:border-pink-500 focus:outline-none transition-colors"
              required
            />
          </div>
          
          <div>
            <label className="block font-medium mb-2 text-gray-700">Work Email *</label>
            <input
              type="email"
              name="email"
              placeholder="your.email@company.com"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:border-pink-500 focus:outline-none transition-colors"
              required
            />
          </div>
          
          <div>
            <label className="block font-medium mb-2 text-gray-700">Job Details & Requirements</label>
            <textarea
              name="message"
              placeholder="Please share:
• Position title and responsibilities
• Required skills and experience
• Salary range or budget
• Work arrangement (remote/hybrid/onsite)
• Project timeline
• Any specific requirements"
              value={formData.message}
              onChange={handleInputChange}
              className="w-full border border-gray-300 px-4 py-3 rounded-lg resize-none focus:border-pink-500 focus:outline-none transition-colors"
              rows="6"
            />
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white py-3 px-6 rounded-lg text-lg font-medium transition-all transform hover:scale-105 disabled:opacity-50 disabled:transform-none flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Sending Inquiry...
              </>
            ) : submitStatus === 'success' ? (
              <>
                <FaCheckCircle className="mr-2" />
                Sent Successfully!
              </>
            ) : (
              <>
                <FaPaperPlane className="mr-2" />
                Send Hiring Inquiry
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="px-6 pb-6 bg-gray-50 rounded-b-xl">
          <div className="text-center mb-3">
            <p className="text-xs text-gray-600 mb-2">Prefer direct contact?</p>
            <div className="flex justify-center space-x-4 text-sm">
              <a href="mailto:sabarisandeveloper@gmail.com" className="text-pink-600 hover:underline flex items-center">
                📧 Email
              </a>
              <a href="tel:+916383767287" className="text-pink-600 hover:underline flex items-center">
                📱 Call
              </a>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">
              💼 Available for: Full-time • Contract • Freelance • Remote opportunities
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HireMePopup;